import mealMenuService from '@/api/mealmenu'
import { MEAL_MENU } from '../types'
import utils from '@/utils/utils.js'
import strings from '@/strings'

export default {
  namespaced: true,
  state: {
    mealMenus: [],
    mealsName: [],
    dishesName: [],
    isDownloadingFile: false,
    fileStatus: strings.uploadStatus.waiting,
    fileId: ''
  },
  getters: {
  },
  mutations: {
    [MEAL_MENU.SET_MEAL_MENUS](state, mealMenus) {
        state.mealMenus = mealMenus
    },
    [MEAL_MENU.SET_MEALS_NAME](state, mealsName) {
      state.mealsName = mealsName
    },
    [MEAL_MENU.SET_DISHES_NAME](state, dishesName) {
      state.dishesName = dishesName
    },
    [MEAL_MENU.SET_IS_DOWNLOADING_FILE](state, value) {
      state.isDownloadingFile = value
    },
    [MEAL_MENU.SET_FILE_STATUS](state, status) {
      state.fileStatus = status
    },
    [MEAL_MENU.SET_FILE_ID](state, fileId) {
      state.fileId = fileId
    }
  },
  actions: {
    async getMealMenus({commit, dispatch}) {
      try {
        dispatch('Global/showLoading', null, {root: true})
        let response = await mealMenuService.getMealMenus()
        commit(MEAL_MENU.SET_MEAL_MENUS, response.data)
      } catch (error) {
        commit(MEAL_MENU.SET_MEAL_MENUS, [])
        return Promise.reject(error)
      } finally {
        dispatch('Global/hideLoading', null, {root: true})
      }
    },
    async getMealsFromMenu({commit, dispatch}, id) {
      try {
        dispatch('Global/showLoading', null, {root: true})
        let response = await mealMenuService.getMealsFromMenu(id)
        return response.data
      } catch (error) {
        commit(MEAL_MENU.SET_MEALS_FROM_MENU, [])
        return Promise.reject(error)
      } finally {
        dispatch('Global/hideLoading', null, {root: true})
      }
    },
    async getMealsName({commit, dispatch}) {
      try {
        dispatch('Global/showLoading', null, {root: true})
        let response = await mealMenuService.getMealsName()
        commit(MEAL_MENU.SET_MEALS_NAME, response.data)
      } catch (error) {
        commit(MEAL_MENU.SET_MEAL_NAME, [])
        return Promise.reject(error)
      } finally {
        dispatch('Global/hideLoading', null, {root: true})
      }
    },
    async getDishesName({commit, dispatch}, id) {
      try {
        dispatch('Global/showLoading', null, {root: true})
        let response = await mealMenuService.getDishesName(id)
        commit(MEAL_MENU.SET_DISHES_NAME, response.data)
      } catch (error) {
        commit(MEAL_MENU.SET_DISHES_NAME, [])
        return Promise.reject(error)
      } finally {
        dispatch('Global/hideLoading', null, {root: true})
      }
    },
    async addDishes({ dispatch }, meal) {
      dispatch('Global/showLoading', null, { root: true })
      try {
        await mealMenuService.addDishes(meal)
        await dispatch('getMeals')
      }
      catch (error) {
        dispatch('Global/hideLoading', null, { root: true })
        return Promise.reject(error)
      }
    },
    async editDishes({ dispatch }, meal) {
      dispatch('Global/showLoading', null, { root: true })
      try {
        await mealMenuService.editDishes(meal)
        await dispatch('getMealMenus')
      }
      catch (error) {
        dispatch('Global/hideLoading', null, { root: true })
        return Promise.reject(error)
      }
    },
    deleteMealMenu: function ({dispatch}, id) {
      dispatch('Global/showLoading', null, {root: true})
      return new Promise((resolve, reject) => {
        mealMenuService.deleteMealMenu(id).then(() => {
          resolve()
        }).catch((error) => {
          reject(error)
        }).finally(
          dispatch('Global/hideLoading', null, {root: true})
        )
      })
    },
    async downloadMealMenuTemplate({dispatch}) {
      try {
        let response = await mealMenuService.getMealMenuTemplateFile()
        let contentType = response.headers['content-type']
        let contentDisposition = response.headers['content-disposition']
        let fileName = utils.getFileName(contentDisposition)
        let blob = new Blob([response.data], {type: contentType})
        utils.handleDownload(blob, fileName)
      } catch (error) {
        dispatch('Global/showMessage', 'errorDefault', {root: true})
      }
    },
    async downloadMealMenuFile({dispatch}) {
      try {
        const {
          maxTimeToWait,
          statusProcessed,
          initialRetry,
          initialSumTimeToWait
        } = strings.downloadFile

        const initDownload = async (retries = initialRetry, sumTimeToWait = initialSumTimeToWait) => {
          const response = await mealMenuService.getMealMenuFile()
          if (response.status === statusProcessed) {
            dispatch('setIsDownloadingFile', false)
            let contentType = response.headers['content-type']
            let contentDisposition = response.headers['content-disposition']
            let fileName = utils.getFileName(contentDisposition)
            let blob = new Blob([response.data], {type: contentType})
            utils.handleDownload(blob, fileName)
          } else {
            dispatch('setIsDownloadingFile', true)
            const result = await utils.exponentialBackoffProgress(retries, sumTimeToWait, maxTimeToWait)
            await utils.waitFor(result.timeToWait);
            return initDownload(retries + 1, result.newSumTimeToWait, result.progress);
          }
        }
        await initDownload();
      } catch (error) {
        dispatch('setIsDownloadingFile', false)
        dispatch('Global/showMessage', 'errorDefault', {root: true})
      }
    },
    setIsDownloadingFile({commit}, value) {
      commit(MEAL_MENU.SET_IS_DOWNLOADING_FILE, value)
    },
    setFileStatus({commit}, status) {
      commit(MEAL_MENU.SET_FILE_STATUS, status)
    },
    async saveFile({dispatch}, file) {
      dispatch('setFileStatus', strings.uploadStatus.uploading)
      try {
        await mealMenuService.importMealMenuFile(file)
        dispatch('setFileStatus', strings.uploadStatus.success)
      } catch (error) {
        dispatch('setFileStatus', strings.uploadStatus.waiting)

        const errorResponse = error.response
        if (errorResponse) {
          let errors = errorResponse.data.error.errors[0]

          const errorReason = errors.reason
          const errorStatus = errorResponse.status

          if (errorStatus === 422) {
            switch (errorReason) {
              case 'unprocessableEntityError':
                dispatch('setFileStatus', strings.uploadStatus.error)
                dispatch('setFileId', errors.data.errorFileId)
                break
            }
          }
        }
      }
    },
    setFileId({commit}, fileId) {
      commit(MEAL_MENU.SET_FILE_ID, fileId)
    },
    async getFileErrors({state}) {
      let response = await mealMenuService.getMealMenuFileErrors(state.fileId)
      let contentType = response.headers['content-type']
      let blob = new Blob([response.data], {type: contentType})
      utils.handleDownload(blob, 'file-errors.xlsx')
    }
  }
}
